import {
  Column,
  Filter,
  FilterArguments,
  FilterCallback,
  OperatorType,
  OperatorString,
  SearchTerm,
} from 'angular-slickgrid';
import { createPopper, Instance as PopperInstance } from '@popperjs/core';
import { localizeWithFallback } from '../../utils/localization-utils';

/**
 * Mode configuration for filter
 */
export interface ModeOption {
  id: string;
  label: string;
  default?: boolean;
}

/**
 * Filter value structure
 */
export interface RangeValue<T> {
  mode: string;
  value?: T;
  from?: T;
  to?: T;
}

/**
 * Abstract base class for range filters (number, date, etc.)
 * Handles common UI logic, SlickGrid integration, and Popper.js dropdown
 */
export abstract class BaseRangeFilter<T> implements Filter {
  // SlickGrid interface properties
  grid: any;
  searchTerms: SearchTerm[] = [];
  columnDef!: Column;
  callback!: FilterCallback;
  operator: OperatorType | OperatorString = OperatorType.equal;

  // UI elements
  protected filterElm!: HTMLDivElement;
  protected headerInputElm!: HTMLInputElement;
  protected dropdownElm!: HTMLDivElement;
  protected radioContainerElm!: HTMLDivElement;
  protected inputsContainerElm!: HTMLDivElement;
  protected errorElm!: HTMLDivElement;
  protected applyButtonElm!: HTMLButtonElement;

  // State
  protected _clearFilterTriggered = false;
  protected _shouldTriggerQuery = true;
  protected isDropdownOpen = false;
  protected currentMode = '';
  protected modeOptions: ModeOption[] = [];
  protected popperInstance?: PopperInstance;
  protected localizationService?: any;
  protected inputEmptyValue = '';
  private boundHandleOutsideClick?: (e: MouseEvent) => void;
  protected pendingValues: SearchTerm[] | null = null;

  /**
   * Abstract methods - must be implemented by child classes
   */
  protected abstract createInputsForMode(mode: string): HTMLElement;
  protected abstract getValuesFromInputs(): any[];
  protected abstract setValuesInInputs(values: any[]): void;
  protected abstract formatDisplayValue(values: any[]): string;
  protected abstract getOperatorForMode(mode: string): OperatorType;

  /**
   * Initialize the filter
   */
  init(args: FilterArguments): void {
    if (!args) {
      throw new Error(
        '[Angular-Slickgrid] A filter must always have an "init()" with valid arguments.'
      );
    }

    this.grid = args.grid;
    this.callback = args.callback;
    this.columnDef = args.columnDef;
    this.operator = args.operator || OperatorType.equal;
    this.searchTerms = args.searchTerms || [];

    // Get optional params from column filter params
    const filterParams = this.columnDef?.filter?.params as any;
    if (filterParams) {
      this.localizationService = filterParams.localizationService;
      this.inputEmptyValue = filterParams.inputEmptyValue ?? '';
    }

    // Get mode options from child class
    this.modeOptions = this.getModeOptions();
    this.currentMode =
      this.modeOptions.find(m => m.default)?.id || this.modeOptions[0]?.id || '';

    // Create the filter DOM element
    this.filterElm = this.createDomElement(args);
    this.updateHeaderInputText();
  }

  /**
   * Get mode options - can be overridden by child if needed
   */
  protected getModeOptions(): ModeOption[] {
    // Default implementation - child can override
    return [
      {
        id: 'equal',
        label: localizeWithFallback(this.localizationService, 'LanguageService::EqualTo'),
        default: true,
      },
      {
        id: 'range',
        label: localizeWithFallback(this.localizationService, 'LanguageService::Between'),
      },
    ];
  }

  protected onModeChanged(): void {
    // no-op por defecto
  }
  /**
   * Clear the filter
   */
  clear(shouldTriggerQuery = true): void {
    if (this.filterElm) {
      this._clearFilterTriggered = true;
      this._shouldTriggerQuery = shouldTriggerQuery;
      this.searchTerms = [];

      // Reset to first mode
      this.currentMode = this.modeOptions[0]?.id || '';
      this.updateModeSelection();
      this.updateInputsForMode();

      // Clear header display
      this.updateHeaderInputText();

      // Close dropdown
      this.closeDropdown();

      if (shouldTriggerQuery) {
        this.applyFilter();
      }
    }
  }

  /**
   * Destroy the filter
   */
  destroy(): void {
    // Destroy Popper instance
    if (this.popperInstance) {
      this.popperInstance.destroy();
      this.popperInstance = undefined;
    }

    if (this.filterElm) {
      if (this.boundHandleOutsideClick) {
        document.removeEventListener('click', this.boundHandleOutsideClick);
        this.boundHandleOutsideClick = undefined;
      }
      this.filterElm.remove();
    }

    // Remove dropdown from body
    if (this.dropdownElm && this.dropdownElm.parentNode) {
      this.dropdownElm.parentNode.removeChild(this.dropdownElm);
    }
  }

  /**
   * Get current filter values
   */
  getValues(): any {
    return this.getValuesFromInputs();
  }

  /**
   * Set filter values programmatically
   */
  setValues(values: SearchTerm[] | SearchTerm): void {
    if (Array.isArray(values) && values.length > 0) {
      this.pendingValues = values;
      this.searchTerms = values;

      // Try to apply immediately if inputs exist
      if (this.areInputsAvailable()) {
        this.setValuesInInputs(values);
        this.updateHeaderInputText();
        this.pendingValues = null;
      } else {
        // Just update the header display without setting inputs
        this.updateHeaderInputText();
      }
    }
  }

  /**
   * Check if inputs are currently available in the DOM
   */
  protected abstract areInputsAvailable(): boolean;

  /**
   * Create the DOM element for the filter
   */
  private createDomElement(args: FilterArguments): HTMLDivElement {
    const fieldId = String(this.columnDef?.id ?? '');

    // Create main container
    const containerElm = document.createElement('div');
    containerElm.className = 'custom-filter-wrapper';

    // Create header input (readonly, shows current filter)
    this.headerInputElm = document.createElement('input');
    this.headerInputElm.type = 'text';
    this.headerInputElm.className =
      'form-control filter-header-input search-filter slick-filter slick-filter-custom';
    this.headerInputElm.placeholder = 'Filtrar';
    this.headerInputElm.readOnly = true;

    const iconSpan = document.createElement('span');

    // Create dropdown container
    this.dropdownElm = document.createElement('div');
    this.dropdownElm.className = 'custom-filter-dropdown form-control-container';
    this.dropdownElm.style.display = 'none';

    // Create radio buttons container
    this.radioContainerElm = document.createElement('div');
    this.radioContainerElm.className = 'filter-mode-selector';
    this.radioContainerElm.style.display = 'flex';
    this.radioContainerElm.style.flexDirection = 'column';
    this.createRadioButtons(fieldId);
    this.boundHandleOutsideClick = this.handleOutsideClick.bind(this);
    document.addEventListener('click', this.boundHandleOutsideClick);

    // Create error message
    this.errorElm = document.createElement('div');
    this.errorElm.className = 'input-error text-danger small mt-1';

    // Create apply button
    const applyLabel = localizeWithFallback(this.localizationService, 'LanguageService::Apply');
    this.applyButtonElm = document.createElement('button');
    this.applyButtonElm.className = 'btn btn-primary btn-sm apply-button';
    this.applyButtonElm.textContent = applyLabel;
    this.applyButtonElm.type = 'button';
    this.applyButtonElm.disabled = false;

    // Add all elements to dropdown
    this.dropdownElm.appendChild(this.radioContainerElm);
    this.dropdownElm.appendChild(this.errorElm);
    this.dropdownElm.appendChild(this.applyButtonElm);

    // Add header input to container
    containerElm.appendChild(this.headerInputElm);
    containerElm.appendChild(iconSpan);

    // Add dropdown to document.body
    document.body.appendChild(this.dropdownElm);

    // Event listeners
    this.headerInputElm.addEventListener('click', e => {
      e.stopPropagation();
      this.toggleDropdown();
    });

    this.applyButtonElm.addEventListener('click', e => {
      e.stopPropagation();
      this.applyFilter();
    });

    // Prevent dropdown from closing when clicking inside it
    this.dropdownElm.addEventListener('click', e => {
      e.stopPropagation();
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleOutsideClick.bind(this));

    // Close dropdown on Escape
    this.dropdownElm.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.closeDropdown();
    });

    // Set initial values if provided
    if (args.searchTerms && args.searchTerms.length > 0) {
      this.setValues(args.searchTerms);
    }

    // Add the filter to the header row
    if (args.filterContainerElm) {
      args.filterContainerElm.appendChild(containerElm);
    }

    return containerElm;
  }

  /**
   * Create radio buttons for mode selection
   */
  private createRadioButtons(fieldId: string): void {
    this.modeOptions.forEach((mode, index) => {
      const labelElm = document.createElement('label');
      labelElm.className = 'filter-radio-label form-check-label';
      labelElm.setAttribute('data-mode', mode.id);

      const radioElm = document.createElement('input');
      radioElm.type = 'radio';
      radioElm.name = `filter-mode-${fieldId}`;
      radioElm.value = mode.id;
      radioElm.checked = mode.id === this.currentMode;
      radioElm.className = 'filter-radio form-check-input';

      radioElm.addEventListener('change', () => {
        this.currentMode = mode.id;
        this.updateInputsForMode();
        this.onModeChanged();
      });

      labelElm.appendChild(radioElm);
      labelElm.appendChild(document.createTextNode(` ${mode.label}`));
      this.radioContainerElm.appendChild(labelElm);

      // Create inputs container for each mode
      const inputsContainerElm = document.createElement('div');
      inputsContainerElm.className = `filter-inputs filter-inputs-${mode.id}`;
      inputsContainerElm.setAttribute('data-mode', mode.id);
      inputsContainerElm.style.display = mode.id === this.currentMode ? 'flex' : 'none';

      const inputsElm = this.createInputsForMode(mode.id);
      inputsContainerElm.appendChild(inputsElm);

      this.radioContainerElm.appendChild(inputsContainerElm);
    });

    // Update order after creating all
    this.updateDisplayInputs();
  }
  /**
   * Update mode selection in UI
   */
  protected updateModeSelection(): void {
    const radios = this.radioContainerElm.querySelectorAll('input[type="radio"]');
    radios.forEach(radio => {
      (radio as HTMLInputElement).checked =
        (radio as HTMLInputElement).value === this.currentMode;
    });

    // Update flex order
    this.updateDisplayInputs();
  }

  /**
   * Update inputs based on selected mode
   */
  protected updateInputsForMode(): void {
    // Update visibility of all inputs containers
    const allInputsContainers = this.radioContainerElm.querySelectorAll<HTMLElement>(
      '[class^="filter-inputs-"]'
    );
    allInputsContainers.forEach(container => {
      const mode = container.dataset.mode;
      const isVisible = mode === this.currentMode;
      container.style.display = isVisible ? 'flex' : 'none';

      // Recreate inputs if visible
      if (isVisible && mode !== undefined) {
        container.innerHTML = '';
        const inputsElm = this.createInputsForMode(mode);
        container.appendChild(inputsElm);
      }
    });

    // Update order for flex layout
    this.updateDisplayInputs();
  }

  /**
   * Update flex order to show inputs below corresponding radio
   */
  private updateDisplayInputs(): void {
    const labels = this.radioContainerElm.querySelectorAll('.filter-radio-label');

    let orderIndex = 0;

    labels.forEach(label => {
      const mode = (label as HTMLElement).getAttribute('data-mode');

      // Set radio label order
      (label as HTMLElement).style.order = String(orderIndex);
      orderIndex++;

      // Find inputs container with matching data-mode
      const inputsContainer = this.radioContainerElm.querySelector(
        `[data-mode="${mode}"][class*="filter-inputs-"]`
      );
      if (inputsContainer) {
        (inputsContainer as HTMLElement).style.order = String(orderIndex);
        // Update visibility here too
        const isVisible = mode === this.currentMode;
        (inputsContainer as HTMLElement).style.display = isVisible ? 'flex' : 'none';
        orderIndex++;
      }
    });
  }

  /**
   * Apply the filter
   */
  private applyFilter(): void {
    const values = this.getValuesFromInputs();

    if (!values || values.length === 0) {
      // No values, clear filter
      this.callback(undefined, {
        columnDef: this.columnDef,
        operator: OperatorType.empty,
        searchTerms: [],
        shouldTriggerQuery: this._shouldTriggerQuery,
      });
    } else {
      // Apply filter with operator based on current mode
      const operator = this.getOperatorForMode(this.currentMode);
      this.searchTerms = values;
      this.callback(undefined, {
        columnDef: this.columnDef,
        operator: operator,
        searchTerms: values,
        shouldTriggerQuery: this._shouldTriggerQuery,
      });
    }

    // Update header input and close dropdown
    this.updateHeaderInputText();
    this.closeDropdown();
  }

  /**
   * Toggle dropdown visibility
   */
  private toggleDropdown(): void {
    if (this.isDropdownOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  private static openFilterInstance: BaseRangeFilter<any> | null = null;

  /**
   * Open the dropdown
   */
  private openDropdown(): void {
    // Close any other open filter
    if (
      BaseRangeFilter.openFilterInstance &&
      BaseRangeFilter.openFilterInstance !== this
    ) {
      BaseRangeFilter.openFilterInstance.closeDropdown();
    }

    // Mark this as the open instance
    BaseRangeFilter.openFilterInstance = this;

    this.dropdownElm.style.display = 'flex';
    this.isDropdownOpen = true;

    this.filterElm.classList.add('opened');

    // Create or update Popper instance
    if (!this.popperInstance) {
      const boundary = this.grid?.getContainerNode?.() ?? 'clippingParents';
      this.popperInstance = createPopper(this.headerInputElm, this.dropdownElm, {
        placement: 'bottom-start',
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 4],
            },
          },
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['bottom-end', 'top-start', 'top-end'],
              boundary,
              rootBoundary: 'viewport',
              padding: 8,
            },
          },
          {
            name: 'preventOverflow',
            options: {
              padding: 8,
              boundary,
              rootBoundary: 'viewport',
            },
          },
        ],
      });
    } else {
      this.popperInstance.update();
    }

    // Apply pending values if any
    if (this.pendingValues) {
      this.setValuesInInputs(this.pendingValues);
      this.pendingValues = null;
    }
  }

  /**
   * Close the dropdown
   */
  private closeDropdown(): void {
    this.dropdownElm.style.display = 'none';
    this.isDropdownOpen = false;

    this.filterElm.classList.remove('opened');

    // Clear the global reference if this was the open instance
    if (BaseRangeFilter.openFilterInstance === this) {
      BaseRangeFilter.openFilterInstance = null;
    }
  }

  /**
   * Handle clicks outside the filter to close dropdown
   */
  private handleOutsideClick(event: MouseEvent): void {
    if (this.isDropdownOpen) {
      const target = event.target as Node;
      const clickedInFilter = this.filterElm && this.filterElm.contains(target);
      const clickedInDropdown = this.dropdownElm && this.dropdownElm.contains(target);

      if (!clickedInFilter && !clickedInDropdown) {
        this.closeDropdown();
      }
    }
  }

  /**
   * Update the header input text to show current filter
   */
  private updateHeaderInputText(): void {
    const values = this.pendingValues || this.getValuesFromInputs();
    const displayValue = this.formatDisplayValue(values);
    if (displayValue) {
      this.headerInputElm.value = displayValue;
      return;
    }

    this.headerInputElm.value = this.resolveInputEmptyValue();
  }

  private resolveInputEmptyValue(): string {
    const emptyValue = this.inputEmptyValue?.trim();
    if (!emptyValue) {
      return '';
    }

    const resolvedKey = emptyValue.includes('::')
      ? emptyValue
      : `LanguageService::${emptyValue}`;

    return localizeWithFallback(this.localizationService, resolvedKey);
  }
}
